import React, { useEffect } from 'react';
import { Accordion, AccordionItem, Badge, Text } from '../../providers/harmony-scope/lazy-enablers';
import { useMEMTranslation } from '../../utils/text-utils';
import { useMonthDayYearTimeAndZoneWithDefaultTimeZone, useMonthAndDayWithDefaultTimeZone } from '../../utils/date-utils';
import { TDataRefreshDetail } from '../../ducks/api-ducks/data-refresh-details-duck/types-data-refresh-details';
import { getSourceRefreshExplanation, getFrequencyLabelKey, getSourceDisplayName, getSourceNextRefreshText } from './source-name-mapping';
import { dataRefreshPanelGridTemplateColumns } from './layout-constants';
import { useBuildICMProperties, useTrackICMEvent } from '../../services/logging/hooks';
import { EICMSeverity, EICMType } from '../../enums/icm-enum';
import { EServiceName } from '../../ducks/api-ducks/types-shared';
import { useIsFed } from '../../utils/utils-fed';
import { MarkdownLinkText } from '../../shared-components/markdown-link-text';

interface IProps {
  detail: TDataRefreshDetail;
}

const rowStyles: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: dataRefreshPanelGridTemplateColumns,
  gap: '0.75rem',
  padding: '0.75rem 0',
  alignItems: 'start',
};

// Grid items default to min-width:auto, which lets long/unbreakable content overflow the fixed
// minmax(0, …fr) tracks. Resetting min-width to 0 and wrapping keeps every cell inside its track so
// columns stay aligned with the header across the whole table.
const cellStyles: React.CSSProperties = {
  minWidth: 0,
  overflowWrap: 'anywhere',
};

const badgeContainerStyles: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '0.375rem',
  minWidth: 0,
};

const badgeItemStyles: React.CSSProperties = {
  flex: '0 1 auto',
  minWidth: 0,
  maxWidth: '100%',
};

// Styling for the "Why does this refresh ...?" disclosure to match the design mock:
// blue heading + chevron, and the explanation in a light gray box with a blue leading border.
const accordionHeadingColor = 'var(--he-color-primary-500, #0f6cbd)';

const accordionHeadingStyles: React.CSSProperties = {
  color: accordionHeadingColor,
  fontWeight: 600,
};

const explanationBoxStyles: React.CSSProperties = {
  marginTop: '0.5rem',
  padding: '0.75rem 1rem',
  backgroundColor: 'var(--he-color-neutral-100, #f5f5f5)',
  borderInlineStart: `4px solid ${accordionHeadingColor}`,
  color: 'var(--he-color-neutral-700, #424242)',
};

const rowWrapperStyles: React.CSSProperties = {
  borderBottom: '1px solid var(--he-color-neutral-100, #f2f2f2)',
};

const NOT_AVAILABLE = '—';
const warnedMissingExplanationSources = new Set<string>();

const parseApiDateTime = (value?: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};

export const DataRefreshSourceRow = ({ detail }: IProps) => {
  const { t } = useMEMTranslation();
  const trackICMEvent = useTrackICMEvent();
  const buildICMProperties = useBuildICMProperties();
  const monthDayYear = useMonthDayYearTimeAndZoneWithDefaultTimeZone();
  const monthAndDay = useMonthAndDayWithDefaultTimeZone();
  const isFed = useIsFed();

  const renderDateTime = (value?: Date) => {
    if (!value) {
      return <Text appearance="body-text">{NOT_AVAILABLE}</Text>;
    }
    return (
      <>
        <Text appearance="body-text">{monthDayYear(value)}</Text>
      </>
    );
  };

  const displayName = getSourceDisplayName(detail.sourceName, t);
  const explanation = getSourceRefreshExplanation(detail.sourceName, t, isFed);
  const sourceNextRefreshText = getSourceNextRefreshText(detail.sourceName, t);
  const frequencyLabelKey = getFrequencyLabelKey(detail.nextSourceRefreshFrequency);
  const frequencyLabel = frequencyLabelKey ? t(frequencyLabelKey) : detail.nextSourceRefreshFrequency || '';
  const explanationTitle = frequencyLabel
    ? t('dataRefreshWhyTitle', { frequency: frequencyLabel.toLowerCase() })
    : t('dataRefreshWhyTitleWithoutFrequency');
  const lastSourceRefreshDate = parseApiDateTime(detail.lastSourceRefreshDateTime);
  const lastReportRefreshDate = parseApiDateTime(detail.lastReportRefreshDateTime);
  const nextReportRefreshDate = parseApiDateTime(detail.nextReportRefreshDateTime);

  useEffect(() => {
    if (explanation || warnedMissingExplanationSources.has(detail.sourceName)) {
      return;
    }

    warnedMissingExplanationSources.add(detail.sourceName);
    const icmProps = buildICMProperties(EICMType.Dependencies, EICMSeverity.Informational, EServiceName.PERFORMANCE);
    trackICMEvent('Missing refresh explanation mapping', 'Data refresh panel', 'DataRefreshSourceRow', {
      ...icmProps,
      message: `No refresh explanation mapping found for source '${detail.sourceName}'`,
    });
  }, [buildICMProperties, detail.sourceName, explanation, trackICMEvent]);

  const refreshFrequencyText = sourceNextRefreshText ?? (nextReportRefreshDate ? monthAndDay(nextReportRefreshDate) : NOT_AVAILABLE);

  return (
    <div data-testid={`data-refresh-row-${detail.sourceName}`} style={rowWrapperStyles}>
      <div style={rowStyles}>
        <Text appearance="body-text" style={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
          {displayName}
        </Text>
        <div style={badgeContainerStyles}>
          {detail.pccDetails && detail.pccDetails.length > 0 ? (
            detail.pccDetails.map((pcc) => (
              <div key={pcc.pccName} style={badgeItemStyles}>
                <Badge appearance="neutral">{pcc.pccName}</Badge>
              </div>
            ))
          ) : (
            <Text appearance="body-text">{NOT_AVAILABLE}</Text>
          )}
        </div>
        <div style={cellStyles}>{renderDateTime(lastSourceRefreshDate)}</div>
        <div style={cellStyles}>{renderDateTime(lastReportRefreshDate)}</div>
        <Text appearance="body-text" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          {refreshFrequencyText}
        </Text>
      </div>
      {explanation && (
        <Accordion style={{ borderTop: 'none' }}>
          <AccordionItem style={{ color: accordionHeadingColor }}>
            <span slot="heading" style={accordionHeadingStyles}>
              {explanationTitle}
            </span>
            <div style={explanationBoxStyles}>
              <Text appearance="body-text" style={{ color: 'var(--he-color-neutral-700, #424242)' }}>
                <MarkdownLinkText text={explanation} />
              </Text>
            </div>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

export default DataRefreshSourceRow;
